import React, { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { FieldOverrides, FormProps } from '@/components/Form';
import MultiStepForm from '@/components/MultiStepForm';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useFile from '@/modules/tasks/views/TasksDashboard/hooks/useFile';
import { getRandomCardImage } from '@/utils/cardImage';

import { getFormFields, getSectionLayout, validateForm } from './config';
import { getInitialValues, ProjectEditorFormValues } from './config';

const OVERRIDES: FieldOverrides = {
  Section: {
    spacing: 'space-y-40',
    Name: {
      variant: 'heading-s',
    },
    Description: {
      variant: 'body-short-02',
      color: 'text-text-secondary',
    },
    Header: {
      className: 'mb-40 ms-8 space-y-4',
    },
  },
  spacing: {
    row: 'space-y-24',
  },
  MultiStepTabs: {
    className: 'mx-36 mt-4',
  },
};

function Editor({
  setFormikSubmit,
  onSubmit,
  file,
  initialValues,
}: {
  setFormikSubmit: FormProps<ProjectEditorFormValues>['setSubmitRef'];
  onSubmit: FormProps<ProjectEditorFormValues>['onSubmit'];
  /**
   * used to pre-fill the edit file form
   */
  file?: ReturnType<typeof useFile>['file'];
  /**
   * used to pre-fill the form when creating a new file
   */
  initialValues?: Partial<ProjectEditorFormValues>;
}) {
  const {
    [FeatureFlags.EnableClientsTask]: isClientsTaskEnabled,
    [FeatureFlags.EnableProjectManagementPreReleaseFeatures]: isProjectManagementPreReleaseEnabled,
  } = useFeatureFlags();

  const { t } = useTranslation(['Admin', 'Task', 'Projects']);

  const defaultCover = useMemo(() => getRandomCardImage('FILE'), []);

  const fields = useMemo(
    () => getFormFields({ t, projectId: file?.id, defaultCover }),
    [t, file?.id, defaultCover],
  );
  const initialFormValues = useMemo(
    () => getInitialValues(file, initialValues),
    [file, initialValues],
  );

  const sections = useMemo(
    () =>
      getSectionLayout({
        t,
        isClientsTaskEnabled,
        isProjectManagementPreReleaseEnabled,
      }),
    [isClientsTaskEnabled, isProjectManagementPreReleaseEnabled, t],
  );

  const validate = useMemo(() => validateForm(t), [t]);

  return (
    <MultiStepForm<ProjectEditorFormValues>
      hideBackNavigation
      hideNextNavigation
      fields={fields}
      formName="ProjectEditor"
      initialValues={initialFormValues}
      overrides={OVERRIDES}
      sections={sections}
      setSubmitRef={setFormikSubmit}
      stepStateHandler="state"
      validate={validate}
      variant="tabs"
      onSubmit={onSubmit}
    />
  );
}

export default Editor;
